<template>
  <div>
    <global-confirm-box ref="confirmBox" />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredItems.length > 0 || loading">
        <mobile-data-cards
          v-for="(group, warehouseId) in itemsByWarehouse"
          :key="warehouseId"
          :headers="mobileHeaders"
          :items="group.items"
          :loading="loading"
          :title="toLatin(group.warehouseName)"
          item-key="id"
          show-filters
          :fields-per-row="3"
          :show-row-number="false"
          :bold-fields="['name']"
        >
          <template v-slot:item.date="{ item }">
            {{ formatDate(item.date) }}
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              x-small
              :color="isStocktakingCompleted(item) ? 'success' : 'info'"
              dark
            >
              <v-icon left x-small>
                {{
                  isStocktakingCompleted(item) ? "mdi-check-circle" : "mdi-clock-outline"
                }}
              </v-icon>
              {{ isStocktakingCompleted(item) ? $t("completed") : $t("active") }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end">
              <circle-button
                icon="mdi-eye"
                :tooltip="$t('overview')"
                class="mr-2"
                color="primary"
                @click="viewStocktaking(item)"
              />
              <edit-button
                v-if="!isStocktakingCompleted(item)"
                class="mr-2"
                @click="editStocktaking(item)"
              />
              <remove-button
                v-if="!isStocktakingCompleted(item)"
                @click="deleteStocktaking(item)"
              />
            </v-row>
          </template>
        </mobile-data-cards>
      </template>

      <empty-state
        v-if="!loading && filteredItems.length === 0"
        icon="mdi-clipboard-check-outline"
        :title="$t('no_stocktaking')"
        :subtitle="$t('no_stocktaking_yet')"
      >
        <primary-button color="deep-purple" @click="startStocktaking">
          <v-icon left>mdi-plus</v-icon>
          {{ $t("new_stocktaking") }}
        </primary-button>
      </empty-state>

      <div class="fixed-bottom-button">
        <v-btn color="deep-purple" dark block @click="startStocktaking">
          <v-icon class="mr-2">mdi-clipboard-check-outline</v-icon>
          {{ $t("new_stocktaking") }}
        </v-btn>
      </div>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <v-data-table
        v-if="filteredItems.length > 0 || loading"
        :headers="headers"
        :items="filteredItems"
        class="striped-table ordera-box-shadow"
        :loading="loading"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <autocomplete
              v-model="statusFilter"
              :items="statusOptions"
              :label="$t('status')"
              item-text="text"
              item-value="value"
              hide-details
              clearable
              class="mr-4"
              style="max-width: 250px"
            >
              <template v-slot:item="{ item }">
                <v-icon left small :color="item.color">{{ item.icon }}</v-icon>
                {{ item.text }}
              </template>
              <template v-slot:selection="{ item }">
                <v-icon left small :color="item.color">{{ item.icon }}</v-icon>
                {{ item.text }}
              </template>
            </autocomplete>
            <v-spacer></v-spacer>
            <primary-button color="deep-purple" @click="startStocktaking">
              <v-icon left>mdi-clipboard-check-outline</v-icon>
              {{ $t("new_stocktaking") }}
            </primary-button>
          </v-toolbar>
          <v-divider class="mt-2" />
        </template>
        <template v-slot:item.date="{ item }">
          {{ formatDate(item.date) }}
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small :color="isStocktakingCompleted(item) ? 'success' : 'info'" dark>
            <v-icon left small>{{
              isStocktakingCompleted(item) ? "mdi-check-circle" : "mdi-clock-outline"
            }}</v-icon>
            {{ isStocktakingCompleted(item) ? $t("completed") : $t("active") }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              icon="mdi-eye"
              :tooltip="$t('overview')"
              class="mr-2"
              color="primary"
              @click="viewStocktaking(item)"
            />
            <edit-button
              v-if="!isStocktakingCompleted(item)"
              class="mr-2"
              @click="editStocktaking(item)"
            />
            <remove-button
              v-if="!isStocktakingCompleted(item)"
              @click="deleteStocktaking(item)"
            />
          </v-row>
        </template>
      </v-data-table>

      <empty-state
        v-if="!loading && filteredItems.length === 0"
        icon="mdi-clipboard-check-outline"
        :title="$t('no_stocktaking')"
        :subtitle="$t('no_stocktaking_yet')"
      >
        <primary-button color="deep-purple" @click="startStocktaking">
          <v-icon left>mdi-plus</v-icon>
          {{ $t("new_stocktaking") }}
        </primary-button>
      </empty-state>
    </template>

    <list-dialog
      v-model="listDialog"
      :edited-item="editedItem"
      @created="onStocktakingCreated"
      @updated="onStocktakingUpdated"
    />

    <!-- Active stocktaking dialog -->
    <list-view-dialog
      v-model="listViewDialog"
      :list-id="viewItemId"
      @completed="onStocktakingCompleted"
    />

    <!-- Completed stocktaking dialog -->
    <completed-stocktaking-dialog
      v-model="completedStocktakingDialog"
      :list-id="viewItemId"
      :notes="viewItemNotes"
      :completed-at="viewItemCompletedAt"
    />
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import ListDialog from "@/components/warehouse/list/ListDialog.vue";
import ListViewDialog from "@/components/warehouse/list/ListViewDialog.vue";
import CompletedStocktakingDialog from "@/components/warehouse/list/CompletedStocktakingDialog.vue";
import warehouseService from "@/services/warehouse";

export default {
  name: "WarehouseTabStocktaking",
  mixins: [hasSnackbar, hasPrinting, hasExcel],
  components: {
    EmptyState,
    MobileDataCards,
    ListDialog,
    ListViewDialog,
    CompletedStocktakingDialog,
  },
  data() {
    return {
      loading: false,
      items: [],
      listDialog: false,
      editedItem: null,
      listViewDialog: false,
      completedStocktakingDialog: false,
      viewItemId: null,
      viewItemNotes: null,
      viewItemCompletedAt: null,
      statusFilter: null,
    };
  },
  computed: {
    headers() {
      return [
        { text: "ID", value: "id", width: 80, mobile: false },
        { text: this.$t("warehouse.document_number"), value: "name", cols: 4 },
        { text: this.$t("stock"), value: "warehouse.name", mobile: false },
        { text: this.$t("date"), value: "date", cols: 4 },
        { text: this.$t("status"), value: "status", cols: 4 },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "right",
          cols: 4,
        },
      ];
    },
    mobileHeaders() {
      return this.headers.filter((h) => h.mobile !== false);
    },
    itemsByWarehouse() {
      const groups = {};
      this.filteredItems.forEach((item) => {
        const warehouseId = item.warehouse?.id || 0;
        if (!groups[warehouseId]) {
          groups[warehouseId] = {
            warehouseName: item.warehouse?.name || this.$t("stock"),
            items: [],
          };
        }
        groups[warehouseId].items.push(item);
      });
      return groups;
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    statusOptions() {
      return [
        {
          text: this.$t("all"),
          value: null,
          icon: "mdi-format-list-bulleted",
          color: "grey",
        },
        {
          text: this.$t("completed"),
          value: "completed",
          icon: "mdi-check-circle",
          color: "success",
        },
        {
          text: this.$t("active"),
          value: "active",
          icon: "mdi-clock-outline",
          color: "info",
        },
      ];
    },
    filteredItems() {
      let result = this.items;

      // Status filter
      if (this.statusFilter === "completed") {
        result = result.filter((item) => this.isStocktakingCompleted(item));
      } else if (this.statusFilter === "active") {
        result = result.filter((item) => !this.isStocktakingCompleted(item));
      }

      if (this.filters.search) {
        const search = this.filters.search.toLowerCase();
        result = result.filter(
          (item) =>
            (item.name && item.name.toLowerCase().includes(search)) ||
            (item.note && item.note.toLowerCase().includes(search))
        );
      }

      // Date filter
      const { from, to } = this.filters.dates;
      if (from && to) {
        result = result.filter((item) => {
          const itemDate = new Date(item.date);
          const fromDate = new Date(from);
          const toDate = new Date(to);
          return itemDate >= fromDate && itemDate <= toDate;
        });
      }

      return result;
    },
    refreshTrigger() {
      return this.$store.state.warehouse.refreshTrigger;
    },
  },
  watch: {
    refreshTrigger() {
      this.fetchItems();
    },
    "filters.warehouse": {
      handler() {
        this.fetchItems();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const exportHeaders = this.headers.filter((h) => h.value !== "actions");
        const items = this.filteredItems.map((item, index) => ({
          ...item,
          num: index + 1,
          date: this.formatDate(item.date),
        }));
        this.downloadExcel({
          fileName: `${this.$t("stocktaking")}.xlsx`,
          sheets: [
            {
              name: this.$t("stocktaking"),
              title: this.$t("stocktaking"),
              headers: exportHeaders,
              items,
            },
          ],
        });
      }
    },
    isPrinting(isPrinting) {
      if (isPrinting) {
        const printHeaders = this.headers.filter((h) => h.value !== "actions");
        const items = this.filteredItems.map((item, index) => ({
          ...item,
          num: index + 1,
          date: this.formatDate(item.date),
        }));
        this.printDocument({
          title: this.$t("stocktaking"),
          tables: [
            {
              headers: printHeaders,
              items,
            },
          ],
        });
      }
    },
  },
  mounted() {
    this.fetchItems();
  },
  methods: {
    async fetchItems() {
      // filters.warehouse is an array, get all selected warehouses
      const selectedWarehouses = Array.isArray(this.filters.warehouse)
        ? this.filters.warehouse
        : this.filters.warehouse
        ? [this.filters.warehouse]
        : [];

      if (selectedWarehouses.length === 0) {
        this.items = [];
        return;
      }

      this.loading = true;
      try {
        // Fetch stocktaking for all selected warehouses
        const promises = selectedWarehouses.map((warehouse) =>
          warehouseService.getWarehouseStocktaking(warehouse.id)
        );
        const responses = await Promise.all(promises);

        // Combine all items from all warehouses
        this.items = responses.flatMap((response) => response.data.data || []);
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_stocktaking"));
      } finally {
        this.loading = false;
      }
    },
    startStocktaking() {
      this.editedItem = null;
      this.listDialog = true;
    },
    onStocktakingCreated(stocktaking) {
      this.items.unshift(stocktaking);
      this.fetchItems();
    },
    onStocktakingUpdated(stocktaking) {
      const index = this.items.findIndex((item) => item.id === stocktaking.id);
      if (index !== -1) {
        this.$set(this.items, index, stocktaking);
      }
      this.fetchItems();
    },
    onStocktakingCompleted() {
      this.listViewDialog = false;
      this.completedStocktakingDialog = false;
      this.fetchItems();
    },
    viewStocktaking(item) {
      this.viewItemId = item.id;

      if (this.isStocktakingCompleted(item) && item.documents && item.documents[0]) {
        this.viewItemNotes = item.documents[0].notes || null;
        this.viewItemCompletedAt = item.documents[0].created_at || null;
        this.completedStocktakingDialog = true;
      } else {
        this.viewItemNotes = null;
        this.viewItemCompletedAt = null;
        this.listViewDialog = true;
      }
    },
    editStocktaking(item) {
      this.editedItem = { ...item };
      this.listDialog = true;
    },
    async deleteStocktaking(item) {
      const confirmed = await this.$refs.confirmBox
        .open(this.$t("delete_stocktaking"), this.$t("confirm_delete_stocktaking"), {
          color: "error",
        })
        .catch(() => false);

      if (confirmed) {
        try {
          await warehouseService.deleteWarehouseStocktaking(item.warehouse.id, item.id);
          this.items = this.items.filter((i) => i.id !== item.id);
          this.showSuccessSnackbar(this.$t("stocktaking_deleted_successfully"));
        } catch (error) {
          this.showErrorSnackbar(this.$t("error_deleting_stocktaking"));
        }
      }
    },
    formatDate(date) {
      if (!date) return "";
      return this.localeDate(date);
    },
    isStocktakingCompleted(item) {
      return item.documents && item.documents.length > 0;
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  background: white;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 5;
}
</style>
